/*
  global SignInWithApple
*/
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { getActivateCodeFromURL, setActivateCode } from 'helpers/ActivateHelper';
import { isBrowserIE } from 'helpers/GlobalHelper';
import { Events, PageNames, handleAnalytics } from '@foxcorp/delta-web-analytics';
import CTA from '../CTA';
import ForgotPasswordCTA from '../ForgotPasswordCTA';
import Header from '../Header';
import SocialLogin from '../SocialLogin';
import SignInApple from '../SignInApple';
import SignInSeparator from '../SignInSeparator';
import GoogleSignInButton from '../GoogleSignInButton';

import Style from './SignIn.module.scss';

const Signin = ({
  ctaProps,
  facebookGenericError,
  facebookLoginOptionalText,
  facebookLoginText,
  facebookSignupOptionalText,
  facebookSignupText,
  forgotPasswordCTAProps,
  getUseUtagGlobal,
  headerProps,
  language,
  onAppleLinking,
  onAppleSignIn,
  onClose,
  onFieldChange,
  onSignin,
  onSocialFacebookLogin,
  pageViewEvent,
  setAppleEmail,
  setAppleToken,
  setIsAppleEmailShared,
  showFacebookLogin,
  showSignin,
  signinEmail,
  signinEmailError,
  signinGenericError,
  signinPassword,
  signinPasswordError,
  signinShowPassword,
  toggleCreatePassword,
  utagAccountSource,
}) => {
  const {
    global_signInButton: signinButtonText,
    profileSignin_header: headerText,
    profile_emailInputPlaceholder: emailPlaceholder,
    profile_genericErrorSignInMessage: signinErrorMessage,
    profile_genericInvalidError: invalidError,
    profile_passwordInputPlaceholder: passwordPlaceholder,
    profile_emailInvalidError: emailInvalidError,
  } = language || {};

  const didTriggerSegmentProfileEvents = useRef(false);

  useEffect(() => {
    const code = getActivateCodeFromURL();
    if (code) setActivateCode(code);

    if (!didTriggerSegmentProfileEvents.current) {
      pageViewEvent({ pageName: PageNames.PROFILE_SIGN_IN, useUtagGlobal: getUseUtagGlobal() });
      handleAnalytics({
        event: Events.PROFILE_SIGN_IN_LANDING,
        payload: {
          pageName: 'account:profile sign in',
          pageType: 'profile sign in',
        },
      });
      handleAnalytics({
        event: Events.PROFILE_SIGN_IN_STARTED,
        payload: { utagAccountSource },
      });

      didTriggerSegmentProfileEvents.current = true;
    }
  }, [getUseUtagGlobal, pageViewEvent, utagAccountSource]);

  let signinPasswordInput = React.createRef();
  const passwordType = signinShowPassword ? 'text' : 'password';
  let errorText = signinGenericError ? signinErrorMessage : null;

  if (_.toLower(signinGenericError) === 'invalid loginid') {
    errorText = invalidError;
  }

  let passwordErrorText;
  let emailErrorText;

  // local error checking
  if (signinPasswordError) {
    passwordErrorText = signinPasswordError;
  }

  if (signinEmailError) {
    emailErrorText = emailInvalidError;
  }

  return (
    <main className={Style.signIn}>
      <Header {...headerProps} />
      <div className={Style.signInWrap}>
        <h1>{headerText}</h1>
        <GoogleSignInButton onClose={onClose} toggleCreatePassword={toggleCreatePassword} />
        <SocialLogin
          facebookGenericError={facebookGenericError}
          facebookLoginOptionalText={facebookLoginOptionalText}
          facebookLoginText={facebookLoginText}
          facebookSignupOptionalText={facebookSignupOptionalText}
          facebookSignupText={facebookSignupText}
          onSocialFacebookLogin={onSocialFacebookLogin}
          showFacebookLogin={showFacebookLogin}
          showSignin={showSignin}
        />
        {SignInWithApple && !isBrowserIE() && (
          <SignInApple
            onAppleSignIn={onAppleSignIn}
            onAppleLinking={onAppleLinking}
            setAppleEmail={setAppleEmail}
            setAppleToken={setAppleToken}
            setIsAppleEmailShared={setIsAppleEmailShared}
          />
        )}
        {<SignInSeparator />}
        <div className={Style.error}>{errorText}</div>
        <form
          action=""
          method="post"
          onSubmit={e => {
            e.preventDefault();
            onSignin({ isCCPAVerify: false });
            return false;
          }}
        >
          <div className={Style.signupFormInputContainer}>
            <div className={Style.signInPasswordContainer}>
              <input
                className={Style.inputField}
                autoFocus
                type="email"
                name="signinEmail"
                placeholder={emailPlaceholder}
                value={signinEmail}
                onChange={onFieldChange}
                autoCapitalize="none"
              />
              <div className={Style.error}>{emailErrorText}</div>
            </div>
            <div className={Style.signInPasswordContainer}>
              <input
                className={Style.inputField}
                type={passwordType}
                name="signinPassword"
                placeholder={passwordPlaceholder}
                value={signinPassword}
                onChange={onFieldChange}
                ref={input => {
                  signinPasswordInput = input;
                }}
                autoCapitalize="none"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              <label className={Style.passwordLabel}>
                <input
                  className={Style.inputField}
                  name="signinShowPassword"
                  type="checkbox"
                  value={signinShowPassword}
                  onChange={e => {
                    onFieldChange(e);
                    signinPasswordInput.focus();
                  }}
                />
                <span />
              </label>
              <div className={Style.error}>{passwordErrorText}</div>
            </div>
            <button className={Style.btnPrimary}>{signinButtonText}</button>
            <ForgotPasswordCTA {...forgotPasswordCTAProps} />
          </div>
        </form>
        <CTA {...ctaProps} />
      </div>
    </main>
  );
};

Signin.defaultProps = {
  children: {},
  facebookGenericError: null,
  facebookLoginOptionalText: null,
  facebookLoginText: null,
  facebookSignupOptionalText: null,
  facebookSignupText: null,
  href: '',
  mvpdData: null,
  privacyPolicyClickEvent: {},
  showFacebookLogin: null,
  showSignin: null,
  signinDropdownIconTheme: {},
  signinEmail: null,
  signinEmailError: null,
  signinGenericError: null,
  signinMigrationDropdownOpen: false,
  signinPassword: null,
  signinPasswordError: null,
  signinShowPassword: false,
};

Signin.propTypes = {
  children: PropTypes.shape({}),
  ctaProps: PropTypes.shape({}).isRequired,
  facebookGenericError: PropTypes.string,
  facebookLoginOptionalText: PropTypes.string,
  facebookLoginText: PropTypes.string,
  facebookSignupOptionalText: PropTypes.string,
  facebookSignupText: PropTypes.string,
  forgotPasswordCTAProps: PropTypes.shape({}).isRequired,
  getUseUtagGlobal: PropTypes.func,
  headerProps: PropTypes.shape({}).isRequired,
  href: PropTypes.string,
  language: PropTypes.shape({
    global_signInButton: PropTypes.string,
    profileSignin_header: PropTypes.string,
    profile_emailInputPlaceholder: PropTypes.string,
    profile_genericErrorSignInMessage: PropTypes.string,
    profile_genericInvalidError: PropTypes.string,
    profile_passwordInputPlaceholder: PropTypes.string,
  }).isRequired,
  mvpdData: PropTypes.shape({}),
  onAppleLinking: PropTypes.func,
  onAppleSignIn: PropTypes.func,
  onClose: PropTypes.func,
  onFieldChange: PropTypes.func.isRequired,
  onSignin: PropTypes.func.isRequired,
  onSocialFacebookLogin: PropTypes.func.isRequired,
  pageViewEvent: PropTypes.func,
  privacyPolicyClickEvent: PropTypes.shape({}),
  setAppleEmail: PropTypes.func,
  setAppleToken: PropTypes.func,
  setIsAppleEmailShared: PropTypes.func,
  showFacebookLogin: PropTypes.bool,
  showSignin: PropTypes.bool,
  signinDropdownIconTheme: PropTypes.shape({}),
  signinEmail: PropTypes.string,
  signinEmailError: PropTypes.bool,
  signinGenericError: PropTypes.string,
  signinMigrationDropdownOpen: PropTypes.bool,
  signinPassword: PropTypes.string,
  signinPasswordError: PropTypes.string,
  signinShowPassword: PropTypes.bool,
  toggleCreatePassword: PropTypes.func,
  utagAccountSource: PropTypes.string.isRequired,
};

export default Signin;
